import React, {useEffect, useState} from "react";
import {Trash2, Edit, MapPin} from "lucide-react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import ConfirmationDialog from "../Common/ConfirmationDialog";
import {SettingsToolbar, SelectionBar, SettingsTable, StatusBadge, AddAction, ListLoader} from "./SettingsUI";

const AreasList = ({
                       searchTerm,
                       setSearchTerm,
                       debouncedSearchTerm,
                       selectedItems,
                       setSelectedItems,
                       selectedAll,
                       setSelectedAll,
                       showNotification,
                       onCreateArea,
                       onEditArea
                   }) => {
    const queryClient = useQueryClient();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [itemsToDelete, setItemsToDelete] = useState([]);


    // Function to fetch areas from the API
    const fetchAreas = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            const response = await api.get('/areas', {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });

            return response.data || [];
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur lors de la récupération des zones";
            throw new Error(errorMessage);
        }
    };

    // Function to delete areas
    const deleteAreas = async (areaIds) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            const results = await Promise.all(
                areaIds.map(id =>
                    api.delete(`/areas/${id}`, {
                        headers: {
                            'X-account-id': accountId.id,
                            'Accept': 'application/json'
                        }
                    }).catch(err => {
                        return {error: err, id};
                    })
                )
            );

            const failures = results.filter(r => r && r.error);
            if (failures.length > 0) {
                if (failures.length === areaIds.length) {
                    throw new Error("Impossible de supprimer les zones sélectionnées");
                } else {
                    throw new Error(`${failures.length} sur ${areaIds.length} zones n'ont pas pu être supprimées`);
                }
            }

            return areaIds;
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                "Une erreur lors de la suppression des zones";
            throw new Error(errorMessage);
        }
    };

    // Use react-query for delete mutation
    const deleteMutation = useMutation({
        mutationFn: deleteAreas,
        onMutate: () => {
            showNotification('loading', 'Suppression en cours...');
        },
        onSuccess: () => {
            setSelectedItems([]);
            setSelectedAll(false);
            queryClient.invalidateQueries({queryKey: ['areas']});
            showNotification('success', 'Zones supprimées avec succès');
            setShowConfirmDialog(false);
            setItemsToDelete([]);
        },
        onError: (error) => {
            showNotification('error', `Erreur: ${error.message}`);
            setShowConfirmDialog(false);
            setItemsToDelete([]);
        }
    });

    // Use react-query to fetch and cache the data
    const {data: areas = [], isLoading, error} = useQuery({
        queryKey: ['areas', debouncedSearchTerm],
        queryFn: fetchAreas,
    });

    // Filter areas based on search term
    const filteredAreas = areas.filter(area =>
        area.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        area.description?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );

    // Reset when search term changes
    useEffect(() => {
        setSelectedItems([]);
        setSelectedAll(false);
    }, [debouncedSearchTerm, setSelectedItems, setSelectedAll]);

    // Handle select all checkbox
    const handleSelectAll = () => {
        const newSelectedAll = !selectedAll;
        setSelectedAll(newSelectedAll);
        setSelectedItems(newSelectedAll ? filteredAreas.map(item => item.id) : []);
    };

    // Handle individual item selection
    const toggleItem = (areaId) => {
        setSelectedItems((prevSelected) =>
            prevSelected.includes(areaId)
                ? prevSelected.filter((id) => id !== areaId)
                : [...prevSelected, areaId]
        );
    };

    // ids awaiting confirmation: the selection, or a single row from its menu
    const askDelete = (ids) => {
        setItemsToDelete(ids);
        setShowConfirmDialog(true);
    };

    // Handle delete confirmation
    const handleConfirmDelete = () => {
        deleteMutation.mutate(itemsToDelete);
    };

    // Handle delete cancellation
    const handleCancelDelete = () => {
        setShowConfirmDialog(false);
        setItemsToDelete([]);
    };

    const rowActions = (area) => [
        {label: 'Modifier', icon: Edit, onClick: () => onEditArea(area), tone: 'blue'},
        {label: 'Supprimer', icon: Trash2, onClick: () => askDelete([area.id]), tone: 'red', danger: true},
    ];

    const columns = [
        {key: 'name', label: 'Nom', render: (area) => (
            <span className="flex items-center gap-2 min-w-0">
                <span className="w-6 h-6 rounded flex-shrink-0 flex items-center justify-center text-white text-xs"
                      style={{backgroundColor: area.color || '#ccc'}}>{area.icon}</span>
                <span className="truncate">{area.name}</span>
            </span>
        )},
        {key: 'description', label: 'Description', className: 'max-w-xs truncate', render: (area) => area.description || '-'},
        {key: 'active', label: 'Statut', render: (area) => <StatusBadge active={area.active}/>},
    ];

    return (
        <div>
            <AddAction onClick={onCreateArea}/>
            <SettingsToolbar search={searchTerm} onSearch={setSearchTerm} placeholder="Rechercher une zone..."/>

            <SelectionBar count={selectedItems.length} singular="zone sélectionnée" plural="zones sélectionnées"
                          onDelete={() => askDelete(selectedItems)} deleting={deleteMutation.isPending}/>

            {isLoading ? (
                <ListLoader label="Chargement des zones..."/>
            ) : error ? (
                <div className="p-4 text-center text-red-500">Erreur : {error.message}</div>
            ) : (
                <SettingsTable
                    columns={columns}
                    rows={filteredAreas}
                    selection={{selected: selectedItems, onToggle: toggleItem, allSelected: selectedAll, onToggleAll: handleSelectAll}}
                    rowActions={rowActions}
                    onRowClick={onEditArea}
                    empty={{
                        icon: MapPin,
                        title: debouncedSearchTerm ? 'Aucune zone trouvée' : 'Aucune zone',
                        text: debouncedSearchTerm ? 'Ajustez la recherche.' : 'Créez les zones de votre salle (terrasse, salle…).',
                    }}
                />
            )}

            {showConfirmDialog && (
                <ConfirmationDialog
                    title="Confirmation de suppression"
                    message={`Êtes-vous sûr de vouloir supprimer ${itemsToDelete.length} zone${itemsToDelete.length > 1 ? 's' : ''} ? Cette action est irréversible.`}
                    confirmLabel="Supprimer"
                    cancelLabel="Annuler"
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                    danger={true}
                />
            )}
        </div>
    );
};

export default AreasList;
